import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Permissions, useAuth } from '@kp/react-sdk';
import {
  Avatar,
  Divider,
  LogoKP,
  Menu,
  MenuItem,
  Spacing,
  Header as UIHeader,
  classNames,
  createClasses,
  pxToRem,
} from '@kp/react-ui';

import { TenantSwitch } from '@/components/TenantSwitch';
import { withPermissions } from '@/components/withPermissions';
import { ROUTES } from '@/constants/routes';
import { ACCOUNT_APP_URI } from '@/utils/env';

const classes = createClasses({
  header: {
    // biome-ignore lint/plugin/noPxToRemSpacing: 6px/20px header insets are off the spacing scale (per design)
    padding: pxToRem(6, 20, 6, 24),
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 100,
  },
  // equal flex on both sides keeps the logo centred
  side: {
    flex: '1 1 0',
    minWidth: 0,
  },
  logoLink: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: 'inherit',
  },
  rightActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: Spacing['2xs'],
  },
  avatar: {
    marginLeft: Spacing.md,
  },
  open: {
    transform: `rotate(180deg)`,
  },
});

const UserManagementItem = withPermissions(() => {
  const { t } = useTranslation();

  return (
    <MenuItem component={Link} to={ROUTES.userManagement}>
      {t('header.userManagement')}
    </MenuItem>
  );
}, [Permissions.UsersRead]);

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const { signOut, user } = useAuth();

  const handleSignOut = useCallback(() => {
    signOut({
      redirectUri: '',
    });
  }, [signOut]);

  const [firstName, lastName] = (user?.info?.name || '').split(/ /);

  return (
    <UIHeader className={classes.header}>
      <div className={classes.side} />

      <Link
        to={ROUTES.home}
        aria-label={t('header.homeLink')}
        className={classes.logoLink}
      >
        <LogoKP data-testid="logo" />
      </Link>

      <div className={classNames(classes.side, classes.rightActions)}>
        <TenantSwitch />

        <Avatar
          data-testid="avatar"
          firstName={firstName || ''}
          lastName={lastName || ''}
          email={user?.info?.email || ''}
          className={classes.avatar}
        >
          <Menu>
            <MenuItem component="a" href={ACCOUNT_APP_URI} target="_blank">
              {t('header.myAccount')}
            </MenuItem>
            <UserManagementItem />
            <Divider />
            <MenuItem onClick={handleSignOut}>{t('header.signOut')}</MenuItem>
          </Menu>
        </Avatar>
      </div>
    </UIHeader>
  );
};
